import { forwardRef, useEffect, useState } from "react"
import { LexicalText } from "@/schema"
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext"
import { COMMAND_PRIORITY_CRITICAL, INSERT_PARAGRAPH_COMMAND, KEY_ENTER_COMMAND } from "lexical"

import { Editor, EditorRefHandle } from "@/components/lexical"
import type { PlaceholderData } from "@/components/lexical/placeholders"
import { MentionDropdownPlugin, MentionRecord } from "@/components/lexical/plugins/mention-dropdown"

interface TaskTitleEditorProps {
  onChange?: (lexicalText: LexicalText) => void
  placeholder?: string
  initialContent?: LexicalText
  onMentionAdded?: (mention: MentionRecord) => void
  onMentionRemoved?: (mention: MentionRecord) => void
  styleOverrides?: React.CSSProperties | Record<`--${string}`, string>
  /** If mention not open, pressing Enter calls this - typically to focus description */
  onEnterPressNoMention?: () => void
  includeUserMentions?: boolean
  enablePlaceholders?: boolean
  placeholders?: PlaceholderData[]
  editable?: boolean
}

function TaskTitleEnterKeyPlugin({
  isMentionOpen,
  onEnterPressNoMention,
}: {
  isMentionOpen: boolean
  onEnterPressNoMention?: () => void
}) {
  const [editor] = useLexicalComposerContext()

  useEffect(() => {
    // Intercept "Enter"
    const unregisterEnter = editor.registerCommand(
      KEY_ENTER_COMMAND,
      (event: KeyboardEvent) => {
        if (isMentionOpen) {
          return false // let mention plugin handle
        }
        event.preventDefault()
        onEnterPressNoMention?.()
        return true
      },
      COMMAND_PRIORITY_CRITICAL
    )

    // Intercept "paragraph insert" for empty lines
    const unregisterParagraph = editor.registerCommand(
      INSERT_PARAGRAPH_COMMAND,
      () => {
        if (!isMentionOpen) {
          onEnterPressNoMention?.()
          return true
        }
        return false
      },
      COMMAND_PRIORITY_CRITICAL
    )

    return () => {
      unregisterEnter()
      unregisterParagraph()
    }
  }, [editor, isMentionOpen, onEnterPressNoMention])

  return null
}

const TaskTitleEditor = forwardRef<EditorRefHandle, TaskTitleEditorProps>(
  (
    {
      onChange,
      placeholder = "Task title",
      initialContent,
      onMentionAdded,
      onMentionRemoved,
      styleOverrides,
      onEnterPressNoMention,
      includeUserMentions = true,
      enablePlaceholders,
      placeholders,
      editable = true,
    },
    ref
  ) => {
    const [isMentionOpen, setIsMentionOpen] = useState(false)

    const titleEditorStyles = {
      "--lexical-editor-font-size": "20px",
      "--lexical-editor-min-height": "40px",
      "--lexical-editor-font-weight": "600",
      ...styleOverrides,
    }

    const handleChange = (lexicalText: LexicalText) => {
      onChange?.(lexicalText)
    }

    return (
      <Editor
        ref={ref}
        isPlainText={true}
        onChange={handleChange}
        initialContent={initialContent}
        startingPlaceholder={placeholder}
        styleOverrides={titleEditorStyles}
        enablePlaceholders={enablePlaceholders}
        placeholders={placeholders}
        editable={editable}
      >
        <MentionDropdownPlugin
          mentionableTypes={
            includeUserMentions
              ? ["user", "client", "prospect", "contact", "person", "business", "trust"]
              : ["client", "prospect", "contact", "person", "business", "trust"]
          }
          onMentionAdded={onMentionAdded}
          onMentionRemoved={onMentionRemoved}
          onOpenChange={setIsMentionOpen}
        />
        <TaskTitleEnterKeyPlugin isMentionOpen={isMentionOpen} onEnterPressNoMention={onEnterPressNoMention} />
      </Editor>
    )
  }
)

TaskTitleEditor.displayName = "TaskTitleEditor"

export default TaskTitleEditor
